import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock, Loader2, Zap, Plus, Trash2, Rocket, CheckCircle2, AlertCircle, X,
  Repeat, Clock, Settings2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const CATS = [
  { key: "", label: "All" },
  { key: "holiday", label: "Holidays" },
  { key: "appreciation", label: "Appreciation" },
  { key: "seasonal", label: "Seasonal" },
  { key: "sports", label: "Sports" },
];

function RunControls({ event, onStarted }) {
  const [open, setOpen] = useState(false);
  const [concepts, setConcepts] = useState(6);
  const [build, setBuild] = useState(3);
  const [social, setSocial] = useState(true);
  const [mode, setMode] = useState("auto");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.key}/run`, {
        concept_count: Number(concepts), build_count: Number(build), queue_social: social, mode,
      });
      toast.success(`Engine started for ${event.name}`);
      onStarted(data.job_id, event.name);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start engine");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`run-${event.key}`}
        className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5 transition-colors">
        <Rocket className="w-3.5 h-3.5" /> Run engine
      </button>
    );
  }
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 mt-2 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">Autonomous run · {event.name}</span>
        <button onClick={() => setOpen(false)}><X className="w-3.5 h-3.5 text-zinc-400" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-zinc-500">Concepts
          <Input type="number" min="1" max="12" value={concepts} onChange={(e) => setConcepts(e.target.value)} className="h-8 mt-1" data-testid={`concepts-${event.key}`} />
        </label>
        <label className="text-[11px] text-zinc-500">Build packages
          <Input type="number" min="0" max="25" value={build} onChange={(e) => setBuild(e.target.value)} className="h-8 mt-1" data-testid={`build-${event.key}`} />
        </label>
      </div>
      <label className="flex items-center justify-between text-xs">
        <span>Auto-queue social posts</span>
        <Switch checked={social} onCheckedChange={setSocial} data-testid={`social-toggle-${event.key}`} />
      </label>
      <div>
        <div className="text-[11px] text-zinc-500 mb-1">Product mode</div>
        <div className="grid grid-cols-3 gap-1">
          {[["auto", "Auto"], ["personalized", "Person."], ["ready_made", "Ready"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`run-mode-${event.key}-${k}`}
              className={`text-[10px] py-1 rounded border ${mode === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <button onClick={run} disabled={busy} data-testid={`confirm-run-${event.key}`}
        className="w-full h-8 rounded-md bg-zinc-900 hover:bg-zinc-800 disabled:opacity-60 text-white text-xs font-medium flex items-center justify-center gap-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Generate now
      </button>
      <p className="text-[10px] text-zinc-400">Builds run in the background (~30s each). You can leave this page.</p>
    </div>
  );
}

function AutomateControls({ event, schedule, onSaved }) {
  const [open, setOpen] = useState(false);
  const s = schedule || {};
  const [enabled, setEnabled] = useState(!!s.enabled);
  const [lead, setLead] = useState(s.lead_days || 45);
  const [concepts, setConcepts] = useState(s.concept_count || 6);
  const [build, setBuild] = useState(s.build_count || 3);
  const [social, setSocial] = useState(s.queue_social !== false);
  const [mode, setMode] = useState(s.mode || "auto");
  const [busy, setBusy] = useState(false);

  // Schedules load async in the parent, so hydrate the controls whenever the saved schedule
  // arrives/changes — otherwise the toggle stays visually OFF even though it's saved ON.
  useEffect(() => {
    setEnabled(!!s.enabled);
    setLead(s.lead_days || 45);
    setConcepts(s.concept_count || 6);
    setBuild(s.build_count || 3);
    setSocial(s.queue_social !== false);
    setMode(s.mode || "auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.enabled, s.lead_days, s.concept_count, s.build_count, s.queue_social, s.mode]);

  const save = async (nextEnabled) => {
    const en = nextEnabled !== undefined ? nextEnabled : enabled;
    setBusy(true);
    try {
      await api.put(`/schedules/${event.key}`, {
        enabled: en, lead_days: Number(lead), concept_count: Number(concepts),
        build_count: Number(build), queue_social: social, mode,
      });
      setEnabled(en);
      toast.success(en ? `Auto-run on · ${lead}d before` : "Automation off");
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 border-t border-zinc-100 pt-2">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs font-medium text-zinc-600">
          <Repeat className="w-3.5 h-3.5" /> Auto-run
        </label>
        <div className="flex items-center gap-2">
          {enabled && <span className="text-[10px] text-emerald-600 font-medium">{lead}d before</span>}
          <Switch checked={enabled} disabled={busy} onCheckedChange={(v) => save(v)} data-testid={`auto-${event.key}`} />
          <button onClick={() => setOpen((o) => !o)} data-testid={`auto-settings-${event.key}`}><Settings2 className="w-3.5 h-3.5 text-zinc-400 hover:text-zinc-700" /></button>
        </div>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-3 gap-2">
          <label className="text-[10px] text-zinc-500">Days before<input type="number" min="1" max="365" value={lead} onChange={(e) => setLead(e.target.value)} className="mt-0.5 w-full h-7 border border-zinc-200 rounded px-1.5 text-xs" data-testid={`lead-${event.key}`} /></label>
          <label className="text-[10px] text-zinc-500">Concepts<input type="number" min="1" max="12" value={concepts} onChange={(e) => setConcepts(e.target.value)} className="mt-0.5 w-full h-7 border border-zinc-200 rounded px-1.5 text-xs" /></label>
          <label className="text-[10px] text-zinc-500">Build<input type="number" min="0" max="25" value={build} onChange={(e) => setBuild(e.target.value)} className="mt-0.5 w-full h-7 border border-zinc-200 rounded px-1.5 text-xs" /></label>
          <label className="col-span-2 flex items-center gap-2 text-[11px] text-zinc-600"><Switch checked={social} onCheckedChange={setSocial} /> Drip social posts</label>
          <button onClick={() => save()} disabled={busy} className="h-7 rounded bg-zinc-900 text-white text-[11px] font-medium">Save</button>
          <div className="col-span-3">
            <div className="text-[10px] text-zinc-500 mb-1">Product mode</div>
            <div className="grid grid-cols-3 gap-1">
              {[["auto", "Auto"], ["personalized", "Personalized"], ["ready_made", "Ready-made"]].map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`auto-mode-${event.key}-${k}`}
                  className={`text-[10px] py-1 rounded border ${mode === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-zinc-200 text-zinc-600"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CalendarEngine() {
  const [events, setEvents] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", month: 1, day: 1, theme: "family", triggers: "", category: "custom" });
  const [schedules, setSchedules] = useState({});
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const loadSchedules = () => {
    api.get("/schedules").then(({ data }) => {
      const m = {};
      data.forEach((s) => { m[s.event_key] = s; });
      setSchedules(m);
    });
    api.get("/scheduler/status").then(({ data }) => setStatus(data));
  };
  useEffect(() => { loadSchedules(); }, []);

  const load = () => {
    const q = cat ? `?category=${cat}` : "";
    api.get(`/events${q}`).then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [cat]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPoll = (jobId, name) => {
    setJob({ id: jobId, name, status: "running", log: [], completed: 0, total: 0 });
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob({ ...data, name });
        if (["done", "error", "stopped"].includes(data.status)) {
          clearInterval(pollRef.current);
          if (data.status === "done") toast.success(`${name}: built ${data.result?.built || 0} products, ${data.result?.social || 0} posts`);
          else if (data.status === "stopped") toast.warning(`${name}: stopped (cost limit)`);
        }
      } catch { clearInterval(pollRef.current); }
    }, 3000);
  };

  const addEvent = async () => {
    if (!form.name) return;
    await api.post("/events", {
      ...form, month: Number(form.month), day: Number(form.day),
      triggers: form.triggers.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Event added");
    setShowAdd(false);
    setForm({ name: "", month: 1, day: 1, theme: "family", triggers: "", category: "custom" });
    load();
  };

  const removeEvent = async (e) => {
    if (!e.custom) return;
    await api.delete(`/events/${e.id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="calendar-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Autonomous Calendar Engine</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Upcoming events</h1>
          <p className="text-sm text-zinc-500 mt-1">Holidays &amp; major sporting events on the calendar — generate collections automatically.</p>
        </div>
        <button onClick={() => setShowAdd((s) => !s)} data-testid="add-event-btn"
          className="h-10 px-4 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 text-sm font-medium flex items-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> Add custom event
        </button>
      </div>

      {status && (
        <div className="bg-white border border-zinc-200 rounded-lg p-3 mb-5 flex items-center gap-6 text-sm" data-testid="scheduler-status">
          <span className="flex items-center gap-2 text-zinc-600"><Repeat className="w-4 h-4 text-emerald-600" /> <b className="font-semibold">{status.active_schedules}</b> events on auto-pilot</span>
          <span className="flex items-center gap-2 text-zinc-600"><Clock className="w-4 h-4 text-blue-600" /> <b className="font-semibold">{status.scheduled_posts}</b> social posts scheduled</span>
          <span className="text-xs text-zinc-400 ml-auto">Scheduler active · checks every 90s</span>
        </div>
      )}

      {showAdd && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-5 grid md:grid-cols-6 gap-3 items-end" data-testid="add-event-form">
          <label className="text-xs text-zinc-500 md:col-span-2">Event name<Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="event-name-input" /></label>
          <label className="text-xs text-zinc-500">Month<Input type="number" min="1" max="12" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="mt-1" /></label>
          <label className="text-xs text-zinc-500">Day<Input type="number" min="1" max="31" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="mt-1" /></label>
          <label className="text-xs text-zinc-500">Theme
            <select value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="mt-1 w-full h-10 border border-zinc-200 rounded-md px-2 text-sm">
              {["remembrance", "anniversaries", "family", "pets", "hometown", "friendship", "travel", "milestones", "back_to_school", "appreciation"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button onClick={addEvent} data-testid="save-event-btn" className="h-10 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Add</button>
          <label className="text-xs text-zinc-500 md:col-span-6">Triggers (comma separated)<Input value={form.triggers} onChange={(e) => setForm({ ...form, triggers: e.target.value })} placeholder="game day, team pride" className="mt-1" /></label>
        </div>
      )}

      {job && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 mb-5" data-testid="job-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {job.status === "running" ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> :
                job.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                <AlertCircle className="w-4 h-4 text-amber-600" />}
              Engine · {job.name}
            </div>
            <span className="text-xs text-zinc-500">{job.completed}/{job.total} built · {job.status}</span>
          </div>
          {job.total > 0 && (
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mt-3">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round((job.completed / job.total) * 100)}%` }} />
            </div>
          )}
          <div className="mt-3 max-h-32 overflow-y-auto scrollbar-thin space-y-0.5">
            {(job.log || []).map((l, i) => <div key={i} className="text-[11px] font-mono text-zinc-500">{l}</div>)}
          </div>
        </div>
      )}

      <div className="flex gap-1.5 mb-4">
        {CATS.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`cat-${c.key || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${cat === c.key ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {events.map((e) => (
            <div key={e.key || e.id} data-testid={`event-${e.key}`} className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl leading-none">{e.emoji}</span>
                  <div>
                    <div className="font-display font-bold text-sm tracking-tight leading-tight">{e.name}</div>
                    <div className="text-[11px] text-zinc-400">{e.next_date}{e.dates_estimated ? " (approx)" : ""}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={`text-[9px] ${e.category === "sports" ? "bg-orange-100 text-orange-700" : e.category === "appreciation" ? "bg-cyan-100 text-cyan-700" : e.category === "seasonal" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"} hover:opacity-100`}>{e.category}</Badge>
                  {e.custom && <button onClick={() => removeEvent(e)} className="text-zinc-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className={`text-2xl font-display font-bold ${e.days_until <= 30 ? "text-orange-500" : "text-zinc-800"}`}>{e.days_until}</span>
                <span className="text-xs text-zinc-400">days away</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(e.triggers || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500">{t}</span>)}
              </div>
              <div className="mt-3">
                <RunControls event={e} onStarted={startPoll} />
              </div>
              <AutomateControls event={e} schedule={schedules[e.key]} onSaved={loadSchedules} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
